import React, { useState } from 'react';
import { useGetFacilityServices } from '../../../App/customize/hooks/useGetFacilityServices';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import LinesEllipsis from 'react-lines-ellipsis';
import { Button } from 'reactstrap';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';

import constants from '../../../../constants/constants';
import { COLUMN_TYPES, INQUIRY_STATUSES } from '../../../../config/app.config';
import { FooterButtons } from '../../../../shared/components/Modals/FooterButtons';
import { useNestedModal } from '../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import { TextDisplayModal, ImageGalleryModal } from '../../../modals/ContentModals';
import ModalPdfViewer from '../../Common/ModalPdfViewer';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';
import { DataTable } from '../../../modals/ModalTables';
import { changeInquiryStatus } from '../../../../redux/actions/inquiryActions';
import { toggleLoading } from '../../../../redux/actions/commonActions';
import { useGetInquiryMessages } from '../../../../hooks/inquiries/useGetInquiryMessages';

import { InquiryStatusDropdown } from '../Components/InquiryStatusDropdown';
import { useInquiryModalContext } from '../utils/InquiryModalContext';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

export function InquiryMainInfoTab() {
  const messageModal = useNestedModal();
  const imagesModal = useNestedModal();
  const pdfModal = useNestedModal();
  const dispatch = useDispatch();

  const { closeInquiryModal, inquiryData, handleRefetchInquiries, setIsMessageModalOpen } =
    useInquiryModalContext();

  const { data: services } = useGetFacilityServices();
  const isBoardingOnly = !!services?.isBoardingOnly;

const [copiedHistory, setCopiedHistory] = useState(false);

const handleCopyHistory = () => {
  const text = buildMessageHistoryTranscript({
    requestType: 'Inquiry',
    header: {
      parent: inquiryData.parentFullName,
      pet: inquiryData.petName,
      doctor: isBoardingOnly ? '' : inquiryData.doctorFullName,
      topic: inquiryData.topicTitle,
      submitted: inquiryData.dateTime,
    },
    messages,
  });
  navigator.clipboard.writeText(text).then(() => {
    setCopiedHistory(true);
    setTimeout(() => setCopiedHistory(false), 1500);
  });
};

  const { data: messages = [] } = useGetInquiryMessages(
    { inquiryId: inquiryData.id },
    {
      enabled: !!inquiryData.id,
    },
  );

const allImages = (messages || []).flatMap((m) =>
  Array.isArray(m.images) ? m.images : [],
);
const allPdfs = (messages || []).flatMap((m) =>
  Array.isArray(m.pdfs) ? m.pdfs : [],
);

  const isInquiryClosed =
    inquiryData.inquiryStatus === INQUIRY_STATUSES.CLOSED ||
    inquiryData.inquiryStatus === INQUIRY_STATUSES.CLOSED_AUTO;

  const handleStatusChange = async (newStatus) => {
    dispatch(toggleLoading(true));
    await dispatch(changeInquiryStatus(inquiryData.id, newStatus));
    await handleRefetchInquiries();
    dispatch(toggleLoading(false));
  };

  const parentLink = `/client-list/profile/${inquiryData.parentId}?tab=${constants.QUERY_TAB_PARAMS.parent}`;
  const petLink = `/client-list/profile/${inquiryData.parentId}?tab=${constants.QUERY_TAB_PARAMS.petInfo}&${constants.QUERY_PARAMS.pet}=${inquiryData.petId}`;

  const commentsText = (inquiryData.comments || '').trim() || 'No comments provided';

  return (
    <>
      <section className="main-info-tab">
        <div className="scrollable-modal-area no-gap">
          <div className="inquiry-info-section modal-main-info-data">
            <div className="dateTime modal-main-info-field" style={{ gridArea: 'dateTime' }}>
              <p className="field-label">Date and Time</p>
              <p className="field-data ellipsis" title={inquiryData.dateTime}>
                {inquiryData.dateTime}
              </p>
            </div>
            <div className="topicTitle modal-main-info-field" style={{ gridArea: 'topicTitle' }}>
              <p className="field-label">Topic</p>
              <p className="field-data ellipsis" title={inquiryData.topicTitle}>
                {inquiryData.topicTitle}
              </p>
            </div>

            <div className="parentFullName modal-main-info-field" style={{ gridArea: 'parentFullName' }}>
              <p className="field-label">Parent Name</p>
              <p className="field-data text-capitalize">
                <Link to={parentLink} className="ellipsis">
                  {inquiryData.parentFullName}
                </Link>
              </p>
            </div>

            <div className="petName modal-main-info-field" style={{ gridArea: 'petName' }}>
              <p className="field-label">Pet Name</p>
              <p className="field-data text-capitalize">
                <Link to={petLink} className="ellipsis">
                  {inquiryData.petName}
                </Link>
              </p>
            </div>

            <div className="doctorFullName modal-main-info-field" style={{ gridArea: 'doctorFullName' }}>
              <p className="field-label">Doctor</p>
              <p className="field-data ellipsis" title={inquiryData.doctorFullName}>
                {inquiryData.doctorFullName}
              </p>
            </div>

            <div
              className="inquiryStatus modal-main-info-field"
              style={{ gridArea: 'inquiryStatus' }}
            >
              <p className="field-label">Inquiry Status</p>
              <div className="field-data">
                <InquiryStatusDropdown
                  currentStatus={inquiryData.inquiryStatus}
                  onStatusChange={handleStatusChange}
                  className="inquiry-status-dropdown"
                />
              </div>
            </div>

            <div
              className="comments modal-main-info-field"
              style={{ gridArea: 'comments' }}
            >
              <p className="field-label">Client Comments</p>
              <div
                style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                className="field-data"
              >
                <ResponsiveEllipsis
                  text={commentsText}
                  maxLine={9}
                  ellipsis={
                    <span>
                      ...{' '}
                      <button
                        type="button"
                        className="nav-link-style"
                        onClick={() => messageModal.open(commentsText)}
                      >
                        View
                      </button>
                    </span>
                  }
                  trimRight
                  basedOn="letters"
                />
              </div>
            </div>
          </div>

          <div className="message-history-table modal-table">
            <table className="message-history-toolbar">
              <tbody>
                <tr>
                  <td className="toolbar-title-cell">
                    <div className="toolbar-title-row">
                      <h3 className="large">Message History</h3>
                      <Button type="button" className="btn-utility" onClick={handleCopyHistory}>
                        <ContentCopyIcon size={16} aria-hidden />
                        Copy History
                      </Button>
                      {copiedHistory && <span className="copied-hint">Copied!</span>}
                    </div>
                  </td>
                  <td className="action-column">
                    {allImages.length > 0 && (
                      <button
                        type="button"
                        className="nav-link-style history-attachment-link"
                        onClick={() => imagesModal.open({ images: allImages })}
                      >
                        {`Images (${allImages.length})`}
                      </button>
                    )}
                  </td>
                  <td className="action-column">
                    {allPdfs.length > 0 && (
                      <button
                        type="button"
                        className="nav-link-style history-attachment-link"
                        onClick={() => pdfModal.open({ pdfs: allPdfs })}
                      >
                        {`PDFs (${allPdfs.length})`}
                      </button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="messages-container">
              <DataTable
                columns={[
                  {
                    header: 'Date',
                    field: 'date',
                    type: COLUMN_TYPES.TEXT,
                    headerClassName: 'date-column',
                  },
                  {
                    header: 'Time',
                    field: 'time',
                    type: COLUMN_TYPES.TEXT,
                    headerClassName: 'time-column',
                  },
                  {
                    header: (
                      <div className="message-column-header">
                        <span>Messages</span>
                        <span className="client-messages-label">Client messages</span>
                        {!isInquiryClosed && (
                          <Button
                            className="save-update"
                            color="primary"
                            onClick={() => setIsMessageModalOpen(true)}
                          >
                            Create Message
                          </Button>
                        )}
                      </div>
                    ),
                    field: 'message',
                    type: COLUMN_TYPES.TRUNCATED_TEXT,
                    headerClassName: 'stretched-column',
                    maxLines: 10,
                    ellipsisRenderer: (message, rowData) => (
                      <span>
                        ...{' '}
                        <button
                          type="button"
                          className="nav-link-style"
                          onClick={() => messageModal.open(rowData)}
                        >
                          View
                        </button>
                      </span>
                    ),
                  },
                  {
                    header: 'Images',
                    field: 'images',
                    type: COLUMN_TYPES.IMAGES,
                    headerClassName: 'action-column',
                    onImageClick: (images) => imagesModal.open(images),
                    buttonText: (count) => `View (${count})`,
                  },
                  {
                    header: 'PDFs',
                    field: 'pdfs',
                    type: COLUMN_TYPES.PDFS,
                    headerClassName: 'action-column',
                    onPdfClick: (pdfs) => pdfModal.open(pdfs),
                    renderCell: (count) => (count.length ? `View (${count.length})` : '-'),
                  },
                ]}
                rows={
                  messages.map((m) => ({
                    ...m,
                    rowCustomClassName:
                      m.authorType === 'client' && 'client-message',
                  })) || []
                }
                noDataMessage="No message history available for this inquiry."
                noDataColSpan={5}
              />
            </div>
          </div>
        </div>
      </section>

      <FooterButtons onClose={closeInquiryModal} showSaveButton={false} />

      <TextDisplayModal
        modalClassName="centered"
        isOpen={messageModal.isOpen}
        onClose={messageModal.close}
        title="Message"
        content={messageModal?.record?.message || messageModal?.record || ''}
      />

      <ImageGalleryModal
        modalClassName="centered"
        isOpen={imagesModal.isOpen}
        onClose={imagesModal.close}
        title="Image Viewer"
        images={imagesModal?.record?.images || []}
      />

      <ModalPdfViewer
        isOpen={pdfModal.isOpen}
        dataPdf={pdfModal?.record?.pdfs || []}
        onClose={pdfModal.close}
      />
    </>
  );
}
